import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Scale, TrashIcon } from "lucide-react";
import { motion } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import { useLayoutContext } from "../context/LayoutContext";
import { useFetchData } from "../hooks/useFetchData";
import { Button } from "react-bulma-components";
import useFilters from "../hooks/useFilters";
import useSearchAndPagination from "../hooks/useSearchAndPagination";
import usePCComparison from "../hooks/usePCComparison";
import PriceRangeFilter from "../components/ListPage/PriceRangeFilter";
import SortOptions from "../components/ListPage/SortOptions";
import Pagination from "../components/common/Pagination";
import PCListItem from "../components/ListPage/PCListItem";
import FilterComponent from "../components/ListPage/FilterComponent";
import FilterComponentMulti from "../components/ListPage/FilterComponentMulti";
import useOpenPage from "../hooks/useOpenPage";
import FilterCategories from "../components/ListPage/FilterCategories";
import HighlightsSection from "../components/Highlights/HighlightsSection";

const ListPage = () => {
  const {
    searchParams,
    resetFilter,
    setSearchParams,
    handleFilterChange,
    handleSearch: setSearchTerm,
    handlePageChange,
  } = useSearchAndPagination();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { userSettings, page, setPage, error, setError, loading, setLoading } =
    useLayoutContext();
  const { showFilters, handleShowFilters } = useFilters();
  const { categories, compounds, totalPages, filters } = useFetchData(
    searchParams,
    setLoading,
    setError,
  );
  const { selectedPCIdsToCompare, handleAddToCompare } = usePCComparison();
  const { handleOpenPage } = useOpenPage();
  const [showCompare, setShowCompare] = useState(false);

  const toggleCompareModal = () => {
    if (selectedPCIdsToCompare.length >= 2) {
      const ids = selectedPCIdsToCompare.join(";");
      navigate(`/comparar/${ids}`);
    } else {
      setShowCompare(!showCompare);
    }
  };

  const handleSearchSubmit = useCallback(
    (searchTerm: string) => {
      setSearchTerm(searchTerm);
    },
    [setSearchTerm],
  );

  if (!isAuthenticated()) {
    return <div>You must be logged in to edit items.</div>;
  }
  return (
    <>
      {/* Highlights Section - Full Width */}

      <div className="container mx-auto relative flex flex-col sm:flex-row">
        <div
          className={`filters-container m-4 w-full sm:max-w-[350px] ${showFilters ? "block" : "hidden"}`}
        >
          <div className="box top-0">
            <input
              type="text"
              className="input mt-4"
              placeholder="Pesquisar..."
              value={searchParams.search_text || ""}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleSearchSubmit(e.currentTarget.value);
                }
              }}
            />

            <FilterCategories
              categories={categories}
              selectedFilter={searchParams?.categories || []}
              setSelectedFilter={handleFilterChange}
            />
            <Button
              onClick={() => {
                resetFilter();
              }}
              className="mt-4"
            >
              Limpar Filtros
            </Button>
            <FilterComponentMulti
              allFilters={filters?.gpu || []}
              componentName="Placa Gráfica"
              componentTypeKey="gpu"
              lastSelectedFilter={filters?.skip_filter ?? null}
              selectedFilter={searchParams?.gpu || []}
              selectedGroupType={searchParams?.gpu_brand || ""}
              setSelectedFilter={handleFilterChange}
            />
            <FilterComponentMulti
              allFilters={filters?.cpu || []}
              componentName="Processador"
              componentTypeKey="cpu"
              lastSelectedFilter={filters?.skip_filter ?? null}
              selectedFilter={searchParams?.cpu || []}
              selectedGroupType={searchParams?.cpu_brand || ""}
              setSelectedFilter={handleFilterChange}
            />
            <FilterComponent
              allFilters={filters?.memory || []}
              component_name="Memoria"
              componentTypeKey="memory"
              selectedFilter={searchParams?.memory ?? ""}
              lastSelectedFilter={filters?.skip_filter ?? null}
              setSelectedFilter={(value: string) =>
                handleFilterChange("memory", value, null)
              }
            />
            <FilterComponent
              allFilters={filters?.storage || []}
              component_name="Storage"
              componentTypeKey="storage"
              selectedFilter={searchParams?.storage ?? ""}
              lastSelectedFilter={filters?.skip_filter ?? null}
              setSelectedFilter={(value: string) =>
                handleFilterChange("storage", value, null)
              }
            />
          </div>
        </div>
        <div
          id="list"
          className={`items-center px-4 py-2 pb-6 ${showFilters ? "pl-10" : "flex-1 w-full"}`}
        >
          <div className="container mx-auto py-2">
            <HighlightsSection />
          </div>

          <nav
            className="box is-centered is-medium mt-2"
            aria-label="breadcrumbs"
          >
            <ul className="flex flex-wrap gap-6 justify-center">
              <li className="block">
                <button
                  className="button text-gray-500 hover:text-gray-700"
                  onClick={() => handleShowFilters(!showFilters)}
                >
                  {showFilters ? (
                    <span>Fechar Filtros</span>
                  ) : (
                    <span>Mostrar Filtros</span>
                  )}
                </button>
              </li>
              <PriceRangeFilter
                selectedPriceRanges={searchParams?.price_range || []}
                setSelectedPriceRanges={(selectedPriceRanges) => {
                  setSearchParams((prevParams) => ({
                    ...prevParams,
                    price_range: selectedPriceRanges,
                    page: 1,
                  }));
                }}
              />
              <SortOptions
                searchParams={searchParams}
                setSearchParams={(value) => setSearchParams(value)}
              />
            </ul>
          </nav>

          <div
            className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 ${showFilters ? "md:grid-cols-1" : "md:grid-cols-2"}`}
          >
            {loading ? (
              <div className="flex justify-center items-center w-full h-full">
                A carregar ...
              </div>
            ) : (
              <>
                {compounds.map((compound, idx) => (
                  <PCListItem
                    key={`${compound.id}-${idx}`}
                    pc={compound}
                    layoutGrid={true}
                    handleViewDetails={handleOpenPage}
                    handleAddToCompare={handleAddToCompare}
                    selectedPCIdsToCompare={selectedPCIdsToCompare}
                    showCosts={
                      userSettings?.isOperatorUser() &&
                      (userSettings?.generalSettings?.show_cost_price ?? false)
                    }
                  />
                ))}
              </>
            )}
          </div>

          {totalPages > 1 && (
            <Pagination
              currentPage={searchParams?.page || 1}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          )}
        </div>
        {selectedPCIdsToCompare.length > 0 && (
          <motion.div
            className="relative inline-block"
            onMouseEnter={() => setShowCompare(true)}
            onMouseLeave={() => setShowCompare(false)}
          >
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              className={`fixed bottom-4 right-4 text-white font-bold py-3 px-5 rounded-full shadow z-50 flex items-center gap-2 ${
                selectedPCIdsToCompare.length > 1
                  ? " bg-blue-500 hover:bg-blue-700"
                  : "bg-gray-500 cursor-not-allowed"
              }`}
              onClick={toggleCompareModal}
            >
              <Scale className="w-5 h-5" />
              Comparar PCs ({selectedPCIdsToCompare.length})
            </motion.button>
            {showCompare && (
              <motion.button
                initial={{ x: 100, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: 100, opacity: 0 }}
                transition={{ type: "spring", stiffness: 300, damping: 30 }}
                className="fixed bottom-4 right-41 bg-red-500 hover:bg-red-700 text-white py-3 pl-3 pr-20 rounded-full shadow z-25 flex items-center gap-2"
                onClick={() => {
                  handleAddToCompare(null);
                  setShowCompare(false);
                }}
              >
                <TrashIcon className="w-5 h-5" />
              </motion.button>
            )}
          </motion.div>
        )}
      </div>
    </>
  );
};

export default ListPage;
